import { TaskQueue, autoinject, bindingMode } from 'aurelia-framework';
import { Coords, getQuadrantCoordinates, Rect } from 'app/shared/lib/screen';
import { fireEventOn } from 'app/shared/lib/fire-events';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Key, getKey } from 'app/shared/lib/keys';
import { focusPrevious, focusNext } from 'app/shared/lib/focus';

@autoinject()
export class EssContextMenuCustomElement {

    // if set to true, this context menu will only appear when a call to `.open()` is made
    // otherwise, it will open with the user right-clicks on anything inside the parent element
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public manualOpen: boolean = false;

    public readonly element: HTMLElement;
    public contextMenuItems: HTMLCollection;

    protected top: number | null = null;
    protected right: number | null = null;
    protected bottom: number | null = null;
    protected left: number | null = null;
    protected isOpen: boolean = false;

    protected parentElement: HTMLElement | null = null;

    protected $priorFocused: HTMLElement | null = null;

    protected activeIndex = 0;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(element: Element, protected readonly queue: TaskQueue) {
        this.element = element as HTMLElement;
        this.contextMenuItems = this.element.children;
    }

    protected attached() {
        this.parentElement = this.element.parentElement;
        if(this.parentElement !== null) {
            this.parentElement.addEventListener('contextmenu', this.handleContextMenu);
        }
    }

    protected detached() {
        if(this.parentElement !== null) {
            this.parentElement.removeEventListener('contextmenu', this.handleContextMenu);
        }
    }

    ////****************** Events ***************************************************////
    protected handleContextMenu = (event: MouseEvent) => {
        if(this.manualOpen) {
            return;
        }
        if(!event.defaultPrevented) {
            event.preventDefault();
            this.openAtEvent(event);
        }
    };

    protected handleKeyDown(event: KeyboardEvent): true | void {
        const element = event.target as HTMLElement;
        const eventKey = getKey(event as KeyboardEvent);

        switch(true) {
            case (eventKey === Key.Up || eventKey === Key.Left):
                event.stopPropagation();
                if(this.activeIndex == 0) {
                    this.activeIndex = this.contextMenuItems.length - 1;
                } else {
                    this.activeIndex = Math.max(this.activeIndex - 1, 0);
                }
                this.updateFocus();
                return;

            case (eventKey === Key.Down || eventKey === Key.Right):
                event.stopPropagation();
                if(this.activeIndex == this.contextMenuItems.length - 1) {
                    this.activeIndex = 0;
                } else {
                    this.activeIndex = Math.min(this.activeIndex + 1, this.contextMenuItems.length - 1);
                }
                this.updateFocus();
                return;

            case (eventKey === Key.Home):
                event.stopPropagation();
                this.activeIndex = 0;
                this.updateFocus();
                return;

            case (eventKey === Key.End):
                event.stopPropagation();
                this.activeIndex = this.contextMenuItems.length - 1;
                this.updateFocus();
                return;

            case (eventKey === Key.Enter || eventKey === Key.Space):
                event.stopPropagation();
                element.click();
                return;

            case eventKey === Key.Esc:
                event.stopPropagation();
                this.close();
                return;

            case eventKey === Key.Tab:
                event.stopPropagation();
                this.close();

                if(event.shiftKey) {
                    focusPrevious();
                }
                else {
                    focusNext();
                }
                return;
        }
        return true;
    }

    protected checkForClose = (event: Event) => {
        const focusEvent = (event as FocusEvent)
        if(this.contextMenuItems) {
            for(let item of this.contextMenuItems) {
                if(item === focusEvent.relatedTarget) {
                    return;
                }
            }
        }
        this.activeIndex = -1;
        this.close();
    };

    protected updateFocus(): void {
        if(!this.contextMenuItems) {
            return;
        }

        Array.from(this.contextMenuItems).forEach((item) => item.classList.remove('active'));
        const item = this.contextMenuItems[this.activeIndex];
        if(item) {
            const element = <HTMLElement>item;
            element.scrollIntoView({ block: 'nearest' });
            item.classList.add('active');
            element.focus();
        }
    }

    ////****************** Functions ************************************************////
    public open() {
        Array.from(this.contextMenuItems)
            .forEach((childItem) => {
                childItem.setAttribute("tabindex", "0");
                childItem.setAttribute("ess-focusable", "");
                childItem.setAttribute("role", "menuitem");
                childItem.addEventListener('blur', this.checkForClose);
            });
        this.isOpen = true;

        (this.contextMenuItems[0] as HTMLElement).focus();  // INFO[Watt]: Focus on the first menu item, skipping over the focus capture helper.
    }

    public close() {
        Array.from(this.contextMenuItems)
            .forEach((childItem) => {
                childItem.removeAttribute("tabindex");
                childItem.removeAttribute("ess-focusable");
                childItem.removeEventListener('blur', this.checkForClose);
            });
        this.isOpen = false;
        this.activeIndex = 0;
        this.element.blur();
        if(this.$priorFocused) {
            this.$priorFocused.focus();
        }
    }

    public openAt({ top, right, bottom, left }: Partial<Rect>, priorFocus?: HTMLElement) {
        this.element.style.top = top ? `${top}px` : '';
        this.element.style.right = right ? `${right}px` : '';
        this.element.style.bottom = bottom ? `${bottom}px` : '';
        this.element.style.left = left ? `${left}px` : '';
        this.$priorFocused = priorFocus ?? null;
        this.open();
    }

    public openAtEvent(event: MouseEvent | KeyboardEvent, priorFocus?: HTMLElement): void {
        let coords: Partial<Rect> = getQuadrantCoordinates({ x: 0, y: 0 });

        // INFO [MW] Check if this is a keyboard event like `keydown` or a programatic mouse click, and
        // determine coordinates from the target element instead of mouse position.
        if(event instanceof KeyboardEvent || (event instanceof MouseEvent && event.x === 0 && event.y === 0)) {
            const element = (event.target as Element);
            const rect = element.getBoundingClientRect();
            const elementCoords: Coords = { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };  // Center of the element
            coords = getQuadrantCoordinates(elementCoords);
        }
        else {
            coords = getQuadrantCoordinates(event);
        }

        if(!priorFocus) { priorFocus = event.target as HTMLElement; }
        this.openAt(coords, priorFocus);
        event.stopImmediatePropagation();
    }

    protected fireEvent(event: string) {
        fireEventOn(this.element, event);
    }
}
